import React, { useEffect, useState } from "react";
import { Sun, CalendarDays, Inbox, Users, Loader2, MapPin, Phone, MessageCircle, TrendingUp, Wallet, Clock } from "lucide-react";
import { api, adminHeaders, waLink } from "@/lib/api";
import { toast } from "sonner";

const money = (n) => (n || n === 0) ? `₪${Number(n).toLocaleString()}` : "—";

const JOB_STATUS_COLORS = {
  Scheduled: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  Confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "In Progress": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Final Inspection": "bg-purple-500/15 text-purple-300 border-purple-500/30",
  Completed: "bg-green-600/15 text-green-300 border-green-600/30",
  Cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="ascendia-card p-4 sm:p-5" data-testid="ops-stat-card">
      <div className="flex items-center gap-2 text-muted-c"><Icon className="h-4 w-4 text-gold-soft" /><p className="text-[11px] uppercase tracking-wide">{label}</p></div>
      <p className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-white">{value}</p>
      {sub && <p className="text-xs text-muted-c mt-0.5">{sub}</p>}
    </div>
  );
}

function JobRow({ j, onOpen }) {
  const fin = j.financials || {};
  const first = (j.client_name || "there").split(" ")[0];
  const waMsg = `Hi ${first}, this is Ascendia Detail regarding your ${j.service || "service"} (ref ${j.ref})${j.appointment_date ? ` on ${j.appointment_date}` : ""}. `;
  return (
    <div className="ascendia-card p-4" data-testid="ops-job-row">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-white truncate">{j.client_name}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${JOB_STATUS_COLORS[j.status] || "border-white/20 text-white/70"}`}>{j.status}</span>
          </div>
          <p className="text-xs font-mono text-gold-soft mt-0.5">{j.ref}</p>
          <p className="text-sm text-secondary mt-1">{j.service || "—"}</p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-c">
            {j.appointment_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {j.appointment_time}</span>}
            {(j.city || j.address) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {[j.address, j.city].filter(Boolean).join(", ")}</span>}
            {j.workers && <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {j.workers}</span>}
          </div>
        </div>
        <button onClick={() => onOpen && onOpen(j)} data-testid="ops-open-job" className="flex-shrink-0 rounded-lg border border-[color:var(--border-subtle)] px-3 py-1.5 text-xs text-white hover:bg-white/5">Open</button>
      </div>

      {/* profitability strip */}
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg border border-[color:var(--border-subtle)] bg-black/30 py-1.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-c">Revenue</p>
          <p className="text-sm font-semibold text-white">{money(fin.revenue)}</p>
        </div>
        <div className="rounded-lg border border-[color:var(--border-subtle)] bg-black/30 py-1.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-c">Profit</p>
          <p className={`text-sm font-semibold ${fin.profit >= 0 ? "text-emerald-300" : "text-red-300"}`}>{money(fin.profit)}</p>
        </div>
        <div className="rounded-lg border border-[color:var(--border-subtle)] bg-black/30 py-1.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-c">Margin</p>
          <p className="text-sm font-semibold text-gold-soft">{fin.margin === null || fin.margin === undefined ? "—" : `${fin.margin}%`}</p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <a href={`tel:${j.client_phone}`} className="inline-flex items-center justify-center gap-1 rounded-lg border border-[color:var(--border-subtle)] py-1.5 text-xs text-white hover:bg-white/5"><Phone className="h-3.5 w-3.5" /> Call</a>
        <a href={waLink(j.client_phone, waMsg)} target="_blank" rel="noopener noreferrer" data-testid="ops-whatsapp" className="inline-flex items-center justify-center gap-1 rounded-lg border border-[color:var(--border-subtle)] py-1.5 text-xs text-white hover:bg-white/5"><MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> WhatsApp</a>
        <span className="inline-flex items-center justify-center text-[11px] text-muted-c">{j.scope_approved ? "Scope ✓" : "Scope pending"}</span>
      </div>
    </div>
  );
}

export default function AdminOperations({ onOpenJob }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/ops/summary", { headers: adminHeaders() })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load operations summary"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="ascendia-card p-6 flex items-center gap-2 text-muted-c"><Loader2 className="h-4 w-4 animate-spin" /> Loading operations…</div>;

  const c = data.counts || {};
  const ct = data.completed_totals || {};
  const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); } catch { return d; } };

  return (
    <div className="space-y-8" data-testid="admin-operations">
      {/* Stat row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={Sun} label="Today's jobs" value={c.today || 0} />
        <StatCard icon={CalendarDays} label="Upcoming jobs" value={c.upcoming || 0} />
        <StatCard icon={Inbox} label="Pending requests" value={c.pending_bookings || 0} sub="awaiting your decision" />
        <StatCard icon={Users} label="New leads" value={c.new_leads || 0} />
      </div>

      {/* Profit summary */}
      <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
        <StatCard icon={Wallet} label="Completed jobs" value={ct.count || 0} />
        <StatCard icon={TrendingUp} label="Revenue (completed)" value={money(ct.revenue)} />
        <StatCard icon={TrendingUp} label="Profit (completed)" value={money(ct.profit)} />
      </div>

      {/* Today */}
      <section>
        <h3 className="font-display text-lg font-semibold text-white mb-3 flex items-center gap-2"><Sun className="h-5 w-5 text-gold-soft" /> Today</h3>
        {data.today.length === 0 ? <p className="text-sm text-muted-c">No jobs scheduled for today.</p> : (
          <div className="grid md:grid-cols-2 gap-4">{data.today.map((j) => <JobRow key={j.id} j={j} onOpen={onOpenJob} />)}</div>
        )}
      </section>

      {/* Upcoming */}
      <section>
        <h3 className="font-display text-lg font-semibold text-white mb-3 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-gold-soft" /> Upcoming</h3>
        {data.upcoming.length === 0 && (data.unscheduled || []).length === 0 ? <p className="text-sm text-muted-c">Nothing upcoming yet.</p> : (
          <div className="grid md:grid-cols-2 gap-4">
            {data.upcoming.map((j) => <JobRow key={j.id} j={j} onOpen={onOpenJob} />)}
            {(data.unscheduled || []).map((j) => <JobRow key={j.id} j={j} onOpen={onOpenJob} />)}
          </div>
        )}
      </section>

      {/* Pending booking requests */}
      <section>
        <h3 className="font-display text-lg font-semibold text-white mb-3 flex items-center gap-2"><Inbox className="h-5 w-5 text-gold-soft" /> Pending booking requests</h3>
        {data.pending_bookings.length === 0 ? <p className="text-sm text-muted-c">No pending requests. Manage all in the Scheduling tab.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.pending_bookings.map((b) => (
              <div key={b.id} className="ascendia-card p-4" data-testid="ops-pending-booking">
                <div className="flex justify-between items-start"><span className="font-semibold text-white">{b.name}</span><span className="text-[10px] px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-300 border-amber-500/30">Pending</span></div>
                <p className="text-xs font-mono text-gold-soft mt-0.5">{b.ref}</p>
                <p className="text-sm text-white mt-1">{fmtDate(b.date)} · <span className="text-gold-soft">{b.slot}</span></p>
                <p className="text-sm text-secondary">{b.service || "—"}</p>
                <p className="text-xs text-muted-c mt-1">{b.phone}</p>
                <a href={waLink(b.phone, `Hi ${(b.name||'there').split(" ")[0]}, this is Ascendia Detail about your request ${b.ref}.`)} target="_blank" rel="noopener noreferrer" className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-[color:var(--border-subtle)] py-1.5 text-xs text-white hover:bg-white/5"><MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> WhatsApp</a>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
